"use client";

import clsx from "clsx";
import { useState, ReactNode } from "react";

import { BagApiBadRequestError, isBagApiBadRequestError } from "@repo/bag";
import { useBag } from "@repo/ui/bag";
import { Button } from "@repo/ui/button";
import { Container } from "@repo/ui/flex";
import * as Sheet from "@repo/ui/sheet";
import { ToastProvider, Toast, ToastViewport } from "@repo/ui/toast";
import { VisuallyHidden } from "@repo/ui/visually-hidden";
import { STRINGS } from "@/constants";
import { CloseIcon } from "@/theme/icons/close";
import { SpinnerIcon } from "@/theme/icons/spinner";
import {
  TYPOGRAPHY_LABEL3,
  TYPOGRAPHY_BODY3_SENTENCE,
  TYPOGRAPHY_HEADING3_BOLD,
  TYPOGRAPHY_HEADING4,
} from "@/theme/topshop/typography/typography-constants";

import classes from "./sheet.module.css";

export type Variant = {
  value: string;
  label: string;
  status: "default" | "disabled" | "alert";
  statusLabel?: string;
};

export type AddToBagSheetProps = {
  variants: Variant[];
  onSuccess?: (variantId: number) => void;
};

type Status = "idle" | "loading" | "success";

const addToBagButtonContent: Record<Status, ReactNode> = {
  idle: "ADD TO BAG",
  loading: <SpinnerIcon label="Adding to bag" />,
  success: "ADDED TO BAG",
};

export const AddToBagSheet = ({ variants, onSuccess }: AddToBagSheetProps) => {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { addProduct } = useBag();

  const addToBag = async (variantId: number) => {
    if (status !== "idle") {
      return;
    }
    setStatus("loading");

    try {
      await addProduct({ variantId });
      onSuccess?.(variantId);
      setStatus("success");

      setTimeout(() => {
        setStatus("idle");
      }, 3000);
    } catch (error) {
      const message = isBagApiBadRequestError(error)
        ? (error as BagApiBadRequestError).bagErrorMessage
        : "Failed to add to bag.";

      setErrorMessage(message);
      setStatus("idle");
    }
  };

  return (
    <Sheet.Root>
      <Sheet.Trigger mute={status !== "idle"} asChild>
        <Button
          variant={status === "success" ? "secondary" : "primary"}
          size="large"
          alignment={status === "loading" ? "center" : "start"}
          fullWidth
          className={TYPOGRAPHY_LABEL3}
          data-testid="add-to-bag-trigger"
        >
          {addToBagButtonContent[status]}
        </Button>
      </Sheet.Trigger>

      <Sheet.Overlay overlayAnimation />

      <Sheet.Content
        position="bottom"
        overlayAnimation
        className={clsx(classes.contentWrapper, TYPOGRAPHY_LABEL3)}
      >
        <Sheet.Close asChild>
          <Button className={classes.closeButton} size="icon">
            <CloseIcon label={STRINGS.sizeSelection.ariaLabels.close} />
          </Button>
        </Sheet.Close>

        <div className={classes.content}>
          <Sheet.Title className={`${classes.title} ${TYPOGRAPHY_HEADING4}`}>
            {STRINGS.sizeSelection.selectSize}
          </Sheet.Title>
          <VisuallyHidden>
            <Sheet.Description>
              {STRINGS.sizeSelection.ariaLabels.selectSizeDescription}
            </Sheet.Description>
          </VisuallyHidden>
          <ul>
            {variants.map(({ value, label, status, statusLabel }) => {
              return (
                <li key={value}>
                  <Container
                    asChild
                    align="center"
                    justify="space-between"
                    className={classes.option}
                  >
                    <Sheet.Close
                      disabled={status === "disabled"}
                      onClick={() => {
                        addToBag(Number(value));
                      }}
                    >
                      <span>{label}</span>

                      {statusLabel && (
                        <span
                          className={clsx(status === "alert" && classes.alert)}
                        >
                          {statusLabel}
                        </span>
                      )}
                    </Sheet.Close>
                  </Container>
                </li>
              );
            })}
          </ul>
        </div>
      </Sheet.Content>

      <ToastProvider>
        <Toast
          message={errorMessage}
          toastTitle={TYPOGRAPHY_HEADING3_BOLD}
          toastMessage={TYPOGRAPHY_BODY3_SENTENCE}
          variant="error"
          closeIcon={
            <CloseIcon label={STRINGS.sizeSelection.ariaLabels.close} />
          }
          onClose={() => setErrorMessage(null)}
          open={!!errorMessage}
        />
        <ToastViewport />
      </ToastProvider>
    </Sheet.Root>
  );
};
